import React, { MouseEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { textPrimary } from '@osmosis/tailwind-ui/styles/text';
import { ButtonProps } from '@osmosis/tailwind-ui/components/Button';
import { IconButtonProps } from '@osmosis/tailwind-ui/components/IconButton';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';

import AddToPlaylistButtonView from './AddToPlaylistButtonView';

import useModal from '../../../hooks/useModal';
import {
  PLAYLIST_TRACK_ACTION_MAP,
  PLAYLIST_TRACK_EVENT,
  usePlaylistContext,
} from '../../../context/PlaylistProvider';
import { useFetch } from '../../../context/FetchProvider';
import { useSnackbarContext } from '../../../context/SnackbarProvider';
import { useUserContext } from '../../../context/UserContext';
import { addToPlaylist } from '../../../lib/frontend/playlist';
import {
  PlaylistLearnNodeItem,
  PlaylistProps,
} from '@osmosis/shared/types/playlist';

import { track } from '../../../lib/util/tracking';
import {
  getAddToPlaylistMessage,
  getRemoveFromPlaylistMessage,
} from '../../../constants/playlist';

type Props = {
  items: PlaylistLearnNodeItem[];
  isIconButton?: boolean;
  buttonProps?: ButtonProps;
  iconButtonProps?: IconButtonProps;
  'data-testid': string;
  isPortal?: boolean;
  onClick?: (e: MouseEvent) => void;
};

/**
 * Component that returns either a button with an icon or one without.
 */
const AddToPlaylistButton = (props: Props) => {
  const {
    isIconButton,
    iconButtonProps = {},
    buttonProps = {},
    items = [],
    onClick,
    isPortal = true,
  } = props;

  const [liveAnnouncement, setLiveAnnouncement] = useState<string>('');
  // Have to create unique tooltip id for each instance and
  // assign after first client load so we don't have server/client mismatch
  const [tooltipId, setTooltipId] = useState<string>();
  useEffect(() => {
    setTooltipId(`tooltip-id-${uuid()}`);
  }, []);

  const { playlists, removePlaylistVideos, updatePlaylists } =
    usePlaylistContext();
  const { onAddSnackBar } = useSnackbarContext();
  const intl = useIntl();
  const { hasPrime, user } = useUserContext();
  const lockoutModalForNonPrime = useModal();
  const lockoutModalForSignedOut = useModal();
  const popupModal = useModal();
  const { toggle } = popupModal;
  const colors = useColors();
  const { post } = useFetch();

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!user) {
      lockoutModalForSignedOut.show();

      return;
    } else if (!hasPrime) {
      lockoutModalForNonPrime.show();

      return;
    }

    onClick?.(e);
    toggle();
  };

  const handleAddToPlaylist = async (playlist: PlaylistProps) => {
    const args = {
      url: playlist.url,
      nodeIDs: items.map((item) => item.learnNodeID),
      special: 0,
    };

    if (playlist.title === 'Watch later' && !args.url) {
      await addToPlaylist(post, {
        ...args,
        special: 1,
      });
    } else {
      await addToPlaylist(post, args);
    }
    const message = getAddToPlaylistMessage({
      intl,
      items,
      playlistName: playlist.title,
    });
    setLiveAnnouncement(message);
    onAddSnackBar({
      message,
      variant: 'bg-gray-800',
      action: {
        name: (
          <FormattedMessage
            id="snackbar.addToPlaylist.undo"
            defaultMessage="Undo"
          />
        ),

        handler: async () => {
          await handleRemoveFromPlaylist(playlist);
        },
        buttonProps: { cn: textPrimary },
      },
    });
    // Update playlists provider
    updatePlaylists();

    track(
      PLAYLIST_TRACK_EVENT,
      {
        action: PLAYLIST_TRACK_ACTION_MAP.add,
        playlistID: args.url,
        is_owner: true,
        is_follower: false,
        source: 'web-add-btn',
        content_type: 'video',
        nodeIDs: args.nodeIDs,
      },
      ['keen', 'pendo'],
    );
  };

  const handleRemoveFromPlaylist = async (playlist: PlaylistProps) => {
    await removePlaylistVideos({
      items,
      playlistName: playlist.title,
      playlistUrl: playlist.url,
    });

    const message = getRemoveFromPlaylistMessage({
      intl,
      items,
      playlistName: playlist.title,
    });
    setLiveAnnouncement(message);
    onAddSnackBar({
      message,
      variant: 'bg-gray-800',
      action: {
        name: (
          <FormattedMessage
            id="snackbar.removeFromPlaylist.undo"
            defaultMessage="Undo"
          />
        ),

        handler: async () => {
          await handleAddToPlaylist(playlist);
        },
        buttonProps: { cn: textPrimary },
      },
    });
    updatePlaylists();
  };

  return (
    <AddToPlaylistButtonView
      user={user}
      nonPrimeLockoutModal={lockoutModalForNonPrime}
      signedOutLockoutModal={lockoutModalForSignedOut}
      popup={popupModal}
      hasPrime={hasPrime}
      isPortal={isPortal}
      playlists={playlists}
      items={items}
      colors={colors}
      isIconButton={!!isIconButton}
      iconButtonProps={iconButtonProps}
      buttonProps={buttonProps}
      hideOnScroll={true}
      onRemove={handleRemoveFromPlaylist}
      onAdd={handleAddToPlaylist}
      onClick={handleClick}
      tooltipId={tooltipId}
      liveAnnouncement={liveAnnouncement}
      data-testid={props['data-testid']}
    />
  );
};

export default AddToPlaylistButton;
